import CurrentPlan from '@/components/CurrentPlan';
import Header from '@/components/Header'
import useDocumentTitle from '@/components/Helmet';
import { useCustomLoader } from '@/components/Loader';
import GlobalAutocomplete from '@/components/material/Autocomplete';
import { useToast } from '@/hooks/use-toast';
import axiosInstance from '@/lib/axiosInstance';
import { formatPrice, formatTableDate } from '@/lib/utils';
import { ExpandMore, HourglassEmpty } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Box, Card, CardContent, Chip, Divider, Grid, Paper, Popover, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { CheckCircle, Code, Eye, ListMinus, NotepadText, PieChart } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

const AdminSubscriptions = () => {
    useDocumentTitle("Payments");
    const loader = useCustomLoader();
    const { toast } = useToast();
    const isMobile = useMediaQuery("(max-width: 600px)");

    const [items, setItems] = useState<any>([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [users, setUsers] = useState([]);
    const [searchUser, setSearchUser] = useState("");
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [expanded, setExpanded] = React.useState<string | false>(false);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | any>(null);

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, row: any) => {
        setAnchorEl(event.currentTarget); // ✅ anchor to the hovered element
        setSelectedRow(row);              // store row data separately
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const fetchItems = async () => {
        loader(true);
        try {
            const { data } = await axiosInstance.get('/api/admin/payments', {
                params: {
                    page,
                    limit,
                    sellerId: selectedUser?._id
                }
            });
            if (data?.success) {
                setItems(data.payments);
                setCount(data.count);
            }
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: "Something went wrong",
            })
        }
        finally {
            loader(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, [page, limit, selectedUser]);

    const fetchUsers = useCallback(async (search: any) => {
        loader(true);
        try {
            await axiosInstance.get('/api/admin/users', {
                params: {
                    page: 1,
                    limit: 100,
                    search,
                    userType: JSON.stringify(["seller", "agent"])
                }
            })
                .then((res) => {
                    setUsers(res.data.users);
                })

        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: "Something went wrong",
            })
        }
        finally {
            loader(false);
        }
    }, [page, limit])

    useEffect(() => {
        const timeout = setTimeout(() => {
            fetchUsers(searchUser);
        }, 500);
        return () => clearTimeout(timeout);
    }, [searchUser]);

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Paper elevation={0} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">Payments</Typography>
                        <div className='w-64'>
                            <GlobalAutocomplete
                                label="Select or Enter User"
                                options={users}
                                value={selectedUser}
                                onChange={setSelectedUser}
                                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                                onInputChange={setSearchUser}
                                placeholder="Select or Enter User"
                            />
                        </div>
                    </Box>
                    <TableContainer style={{ overflowX: 'auto' }}>
                        {isMobile ? <Box>

                            {
                                items?.map((payment: any, index: any) => (
                                    <Accordion
                                        expanded={expanded === payment?._id}
                                        key={payment?._id}
                                        onChange={() => setExpanded(expanded === payment?._id ? null : payment?._id)}
                                        className='mb-2'
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMore />}
                                            aria-controls="panel1-content"
                                            id="panel1-header"
                                            sx={{
                                                "& .MuiAccordionSummary-content": {
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                },
                                                backgroundColor: "#f9fafb",
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Avatar src={payment?.seller?.avatar} />
                                                <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                                                    {payment?.seller?.firstName} {payment?.seller?.lastName}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={payment?.status}
                                                size="small"
                                                sx={{ ml: "auto", width: 100, height: 30 , textTransform: "capitalize"}}
                                                color={payment?.status ? "success" : "error"}
                                            />
                                        </AccordionSummary>

                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Owner Name:</span><br />
                                                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{payment?.seller?.firstName} {payment?.seller?.lastName}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Owner Email:</span><br />
                                                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{payment?.seller?.email}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Owner Phone:</span><br />
                                                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{payment?.seller?.phone}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Price:</span><br />
                                                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{formatPrice(payment?.amountInPaise / 100)}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Payment ID:</span><br />
                                                    <span className="font-semibold">{payment?.paymentId || "N/A"}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Order ID:</span><br />
                                                    <span className="font-semibold">{payment?.orderId || "N/A"}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Payment Date:</span><br />
                                                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{formatTableDate(payment?.createdAt)}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Plan Name:</span><br />
                                                    <span className="font-semibold">
                                                        {payment?.plan?.name || "N/A"}
                                                    </span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span>Plan Code:</span><br />
                                                    <span className="font-semibold">{payment?.plan?.code || "N/A"}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span>Quota:</span><br />
                                                    <span className="font-semibold">{payment?.plan?.quota || "N/A"}</span>
                                                </Grid>                                          
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                ))
                            }
                            {
                                items.length === 0 && <Typography sx={{ textAlign: 'center' }}>No Subscriptions Found</Typography>
                            }
                        </Box> : <Table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: 8 }}>Date</TableCell>
                                    <TableCell style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: 8 }}>Name</TableCell>
                                    <TableCell style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: 8 }}>Phone</TableCell>
                                    <TableCell style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: 8 }}>Plan</TableCell>
                                    <TableCell style={{ textAlign: 'right', borderBottom: '1px solid #ddd', padding: 8 }}>Amount</TableCell>
                                    <TableCell style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: 8 }}>Status</TableCell>
                                    <TableCell style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: 8 }}>Order ID</TableCell>
                                    <TableCell style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: 8 }}>Payment ID</TableCell>
                                    <TableCell style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: 8 }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((row: any) => (
                                    <TableRow key={row._id}>
                                        <TableCell style={{ padding: 8 }}>{formatTableDate(row.createdAt)}</TableCell>
                                        <TableCell style={{ padding: 8 }}>{row.seller?.firstName} {row?.seller?.lastName}</TableCell>
                                        <TableCell style={{ padding: 8 }}>{row.seller?.phone}</TableCell>
                                        <TableCell style={{ padding: 8 }}>{row.plan?.name}</TableCell>
                                        <TableCell style={{ padding: 8, textAlign: 'right' }}>{formatPrice(row.amountInPaise / 100)}</TableCell>
                                        <TableCell style={{ padding: 8 }}>
                                            <Chip
                                                label={row.status}
                                                color="success"
                                                sx={{ textTransform: 'capitalize' }}
                                            />
                                        </TableCell>
                                        <TableCell style={{ padding: 8 }}>{row.orderId}</TableCell>
                                        <TableCell style={{ padding: 8 }}>{row.paymentId || '-'}</TableCell>
                                        <TableCell>
                                            <Box>
                                                <Tooltip title="View Plans" placement='top'>
                                                    <Typography
                                                        aria-owns={open ? 'mouse-over-popover' : undefined}
                                                        aria-haspopup="true"
                                                        onMouseEnter={(e) => handlePopoverOpen(e, row)}
                                                        onMouseLeave={handlePopoverClose}
                                                    >
                                                        <Eye
                                                            style={{ cursor: 'pointer' }}
                                                            className='text-blue-700'
                                                        />
                                                    </Typography>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {items.length === 0 && (
                                    <TableRow><TableCell colSpan={6} style={{ textAlign: 'center' }}>No payments yet</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>}
                        <TablePagination
                            component="div"
                            count={count}
                            page={page - 1}
                            onPageChange={(event, newPage) => setPage(newPage + 1)}
                            rowsPerPage={limit}
                            onRowsPerPageChange={(event) => setLimit(parseInt(event.target.value))}
                        />
                    </TableContainer>
                </Paper>

                <Popover
                    id="mouse-over-popover"
                    sx={{ pointerEvents: 'none' }}
                    open={open}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    onClose={handlePopoverClose}
                    disableRestoreFocus
                >
                    <Card elevation={3} sx={{ borderRadius: 3, p: 2 }}>
                        <CardContent sx={{ p: 0 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    backgroundColor: "#f9fafb",
                                    borderRadius: 2,
                                    p: 2,
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <NotepadText />
                                    <Typography variant="body1">
                                        <b>Plan:</b> {selectedRow?.plan.name}
                                    </Typography>
                                </Box>

                                <Divider />

                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <ListMinus />
                                    <Typography variant="body1">
                                        <b>Total Limit:</b> {selectedRow?.plan.quota}
                                    </Typography>
                                </Box>

                                <Divider />

                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Code />
                                    <Typography variant="body1">
                                        <b>Plan Code:</b>{" "}
                                        {selectedRow?.plan.code}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>

                    </Card>
                </Popover>
            </section>
        </div >
    )
}

export default AdminSubscriptions